import { toast } from "react-toastify";
import React, { useCallback } from 'react';

import { IUser } from "../entities";
import { paths } from "../routes/paths";
import { ILoginRequest } from "../dtos";
import { useRouter } from "../routes/hooks";
import { apiPost, setAuthToken, clearAuthToken } from '../helpers/external-request';
import { getCookie, setCookie, clearCookies } from "../helpers/browser-cookie-manager";

export const USER_KEY = '@@monteflor-user';
export const TOKEN_KEY = '@@monteflor-token';

export interface IAuthHook {
  isLoading: boolean;
  currentUser?: IUser;
  isAuthenticated: boolean;
  handleLogout: () => void;
  getAuthState: () => Promise<void>;
  handleLogin: (payload: ILoginRequest) => Promise<void>;
}

const useAuthentication = (): IAuthHook => {
  const router = useRouter();

  const [currentUser, setCurrentUser] = React.useState<IUser>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);

  const getAuthState = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const token = getCookie(TOKEN_KEY);
      const userStored = getCookie(USER_KEY);

      if (token && userStored) {
        const user = JSON.parse(userStored) as IUser;
        const firstNameInitial = user.firstName?.charAt(0).toUpperCase();
        const lastNameInitial = user.lastName?.charAt(0).toUpperCase();
        setCurrentUser({ ...user, initials: `${firstNameInitial}${lastNameInitial}` });

        await saveCachedUserToken({ token, ...user });
      } else {
        setIsAuthenticated(false);
        // No redirigir aquí, el AuthGuard se encargará de eso cuando sea necesario
      }
    } catch (error) {
      const { message } = error as Error;

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveCachedUserToken = async (data: IUser & { token: string }): Promise<void> => {
    try {
      const { token, ...user } = data;

      setCookie(TOKEN_KEY, token);
      setCookie(USER_KEY, JSON.stringify(user));

      setAuthToken(token);

      setIsAuthenticated(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogin = async (payload: ILoginRequest): Promise<void> => {
    try {
      setIsLoading(true);
      const data = (await apiPost('auth/login', payload)) as unknown as IUser & { token: string };
      
      // Extraer información del usuario
      const { token, ...user } = data;
      
      // Generar iniciales
      const firstNameInitial = user.firstName?.charAt(0).toUpperCase();
      const lastNameInitial = user.lastName?.charAt(0).toUpperCase();
      const userWithInitials = { ...user, initials: `${firstNameInitial}${lastNameInitial}` };
      
      // Actualizar el estado del usuario ANTES de guardar en cookies
      setCurrentUser(userWithInitials);
      
      // Guardar en cookies y configurar auth
      await saveCachedUserToken(data);

      // Obtener returnTo de URL params o usar panel por defecto
      const searchParams = new URLSearchParams(window.location.search);
      const returnTo = searchParams.get('returnTo') || paths.panel.root;

      // Redirigir al panel o a la página de retorno
      router.replace(returnTo);

    } catch (error) {
      const { message } = error as Error;

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);

      // Limpiar estado local
      setCurrentUser(undefined);
      setIsAuthenticated(false);

      // Limpiar cookies y storage
      clearCookies();
      
      // Limpiar token de auth headers
      clearAuthToken();

      // Limpiar cualquier cache/storage adicional
      sessionStorage.clear();
      
      // Redirigir al login
      const href = `${paths.auth.login}`;
      router.replace(href);

      // Mostrar mensaje de confirmación
      toast.success('Sesión cerrada exitosamente');

    } catch (error) {
      console.error('Error durante el logout:', error);
      toast.error('Error al cerrar sesión');
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const memoizedValue = React.useMemo(
    () => ({
      isLoading,
      currentUser,
      isAuthenticated,
    }),
    [isLoading, currentUser, isAuthenticated],
  );

  return {
    ...memoizedValue,
    handleLogin,
    getAuthState,
    handleLogout,
  };
};

export default useAuthentication;
